// Typed wrappers for EnvelopeOps /api/v1 endpoints.
// All transport delegated to @avagency/frontend/lib/api.apiFetch
// (credentials:include, JSON helpers, ApiError with status).
//
// Same-origin cookie session — no CORS credentials needed.
// Dev: vite proxy proxies /api + /v1 to :8100 and injects tenant header.
import { apiFetch, ApiError } from '@avagency/frontend/lib/api';
// AUTH_INVALIDATE_EVENT: fired on any 401 from a non-auth endpoint so
// the auth gate (useMe) re-checks the session within one render cycle.
export const AUTH_INVALIDATE_EVENT = 'envelope-ops:auth:invalidate';
// envelopeFetch wraps apiFetch with the AUTH_INVALIDATE_EVENT side effect.
async function envelopeFetch(path, init) {
    try {
        return await apiFetch(path, init);
    }
    catch (err) {
        if (err instanceof ApiError) {
            if (err.status === 401 && !path.includes('/api/v1/auth/me')) {
                window.dispatchEvent(new CustomEvent(AUTH_INVALIDATE_EVENT));
            }
            const decorated = err;
            decorated.status = err.status;
            throw decorated;
        }
        throw err;
    }
}
export const fetchMe = () => envelopeFetch('/api/v1/auth/me');
export const fetchIntegrations = () => envelopeFetch('/api/v1/integrations');
export const signInURL = (returnTo = '/') => {
    const q = new URLSearchParams({ return_to: returnTo });
    return `/api/v1/auth/signin?${q.toString()}`;
};
export const startDocuSignOAuth = async (returnTo = '/') => {
    const resp = await envelopeFetch('/v1/oauth/docusign/start', { method: 'POST', json: { return_url: returnTo } });
    window.location.href = resp.authorize_url;
};
export const disconnectIntegration = (integrationID) => envelopeFetch(`/api/v1/integrations/${encodeURIComponent(integrationID)}/disconnect`, { method: 'POST' });
export const signOut = async (returnTo = '/') => {
    const q = new URLSearchParams({ return_to: returnTo });
    const res = await fetch(`/api/v1/auth/signout?${q.toString()}`, {
        method: 'POST',
        credentials: 'include',
    });
    const location = res.headers.get('Location') || returnTo;
    window.location.href = location;
};
export const parseOAuthCallbackStatus = () => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('av_oauth_status');
    if (!status)
        return { kind: 'none' };
    if (status === 'success')
        return { kind: 'success' };
    return { kind: 'error', message: params.get('av_oauth_error') ?? 'OAuth flow failed' };
};
export const clearOAuthCallbackStatusFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('av_oauth_status') && !params.has('av_oauth_error'))
        return;
    params.delete('av_oauth_status');
    params.delete('av_oauth_error');
    const search = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (search ? `?${search}` : '') + window.location.hash);
};
export const fetchEnvelopes = (opts) => {
    const q = new URLSearchParams();
    if (opts?.status)
        q.set('status', opts.status);
    if (opts?.integrationID)
        q.set('integration_id', opts.integrationID);
    if (opts?.limit)
        q.set('limit', String(opts.limit));
    const qs = q.toString();
    return envelopeFetch(`/api/v1/envelopes${qs ? `?${qs}` : ''}`);
};
export const fetchBottleneck = (limit) => {
    const q = limit ? `?limit=${limit}` : '';
    return envelopeFetch(`/api/v1/signers/bottleneck${q}`);
};
export const fetchRules = () => envelopeFetch('/api/v1/reminder-rules');
export const putRule = (body) => envelopeFetch('/api/v1/reminder-rules', { method: 'PUT', json: body });
export const bulkNudge = (integrationID, envelopeIDs) => envelopeFetch('/api/v1/envelopes/bulk-nudge', {
    method: 'POST',
    json: { integration_id: integrationID, envelope_ids: envelopeIDs },
});
export const fetchReminderHistory = (envelopeID) => {
    const q = envelopeID ? `?envelope_id=${encodeURIComponent(envelopeID)}` : '';
    return envelopeFetch(`/api/v1/reminders/history${q}`);
};
// Re-export ApiError for instanceof checks.
export { ApiError };
